import React, { useState, useEffect } from "react";

import {
    Box,
    Divider,
    Grid,
    Typography,
    Button,
    styled,
    Card,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableFooter
} from '@mui/material';

import { useNavigate, useParams } from "react-router-dom";

import { useDocuments, documentTypes, targetTypes } from "../../../hooks/api/useDocuments";
import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { useSystemUtils } from "../../../hooks/useSystemUtils";
import { useAppContextFeatures } from "../../../context/AppContext";
import { useSystemRoutes, routeTypes } from "../../../hooks/useSystemRoutes";
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from "../../../hooks/usePermissions";
import {
    getQrCodeDataUrl,
    outputTypes as scanDataOutputTypes,
    scopeTypes as scanDataScopeTypes,
    targetTypes as scanDataTargetTypes
} from "../../../hooks/useScanData";
import { useTranslations } from '../../../hooks/useTranslations';

import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonBreadcumbs from "../../common/CommonBreadcumbs";
import CommonRestricted from "../../common/CommonRestricted";
import DocumentQrCode from "../DocumentQrCode";
import ViewDocumentDependency from "../ViewDocumentDependency";

import { CommonPdf, renderModes, documentTypes as commonPdfDocumentTypes } from "../../common/CommonPdf";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(3)
}));

const StyledLabel = styled(Typography)(({ theme }) => ({
    fontSize: '0.9rem',
    fontWeight: 800
}))

const StyledContent = styled(Typography)(({ theme }) => ({
    fontSize: '0.9rem'
}))

const StyledContent2 = styled(Typography)(({ theme }) => ({
    fontSize: '0.8rem',
    mt: 2
}))

const StyledDivider = styled(Divider)(({ theme }) => ({
    mt: 1,
    mb: 0,
    borderStyle: 'dashed'
}))

export default function ViewTransferRequisition({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@document"] })

    const { id } = useParams();

    const { setSystemLoading, openCustomDrawer } = useAppContextFeatures();

    const { promptMessage } = useSystemPrompt();
    const { get } = useDocuments({ 
        documentType: documentTypes.transferRequisition, 
        targetType: targetTypes.transferRequisition 
    });
    const { formatCurrencyDisplay, currencyAbb } = useSystemUtils();
    const { getRoute } = useSystemRoutes(routeType)
    const { getHtmlHead } = useSystemHead(routeType)
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.document.id, scopeTypes.document.transferRequisition.id)

    const navigate = useNavigate();
    const [data, setData] = useState()

    const fetchData = async () => {

        setSystemLoading(true);

        try {

            let response = await get({ id,  dataTypesArray: ["dependency"] });
            // console.log('response?.data?.items', response?.data)
            setData(response?.data)

        } catch (error) {
            promptMessage(false, `${error.message}`)
        }

        setSystemLoading(false)

    }

    useEffect(() => {

        fetchData();

    }, [])

    const getBreadcumbActionMenus = () => {

        let props = []

        if (isPermissionAllow({ functionType: functionTypes.pageUpdate.id })) {
            props.push({
                displayText: t("editDocument"),
                href: getRoute(`documents/transferRequisition/update/${id}`),
            })
        }

        if (isPermissionAllow({ functionType: functionTypes.pageView.id })) {

            props.push({
                displayText: t("printPreview"),
                onClick: () => {

                    // adding the qrcode into doc data
                    data.qrCodeData = getQrCodeDataUrl()

                    openCustomDrawer({
                        show: true,
                        anchor: "right",
                        title: `Preview PDF`,
                        PaperProps: {
                            sx: { width: 2 / 3 }
                        },
                        // handleOnClose: () => setPreviewPdfOpen(false),
                        ComponentActions: <></>,
                        ComponentBody:
                            <CommonPdf
                                routeType={routeType}
                                renderMode={renderModes.preview.id}
                                documentType={commonPdfDocumentTypes.transferRequisition.id}
                                document={{
                                    title: data?.document?.doc_number
                                }}
                                data={data}
                            // customData={{
                            //     items: data
                            // }}
                            />
                    })

                    return true;

                }
            })

        }

        return props

    }

    const RenderTable = () => {

        if (!data) return <></>

        return (
            <TableContainer
                // component={Paper}
                sx={{
                    minHeight: 400,
                    maxHeight: 400
                }}
            >
                <Table
                    size="small"
                    stickyHeader
                >
                    <TableHead>
                        <TableRow>
                            <TableCell width={20}>#</TableCell>
                            <TableCell width={100}>{t('code')}</TableCell>
                            <TableCell width={250}>{t('name')}</TableCell>
                            <TableCell width={400}>{t('description')}</TableCell>
                            <TableCell align="right">{t('qty')}</TableCell>
                            <TableCell align="right" width={150}>{t('uom')}</TableCell>
                            {/* <TableCell align="right">Price</TableCell> */}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            data?.items.map((row, index) => {

                                return (
                                    <TableRow key={row.id}>
                                        <TableCell>{(index + 1)}</TableCell>
                                        <TableCell>{row.code}</TableCell>
                                        <TableCell>{row.item_name}</TableCell>
                                        <TableCell>{row.description}</TableCell>
                                        <TableCell align="right">{row.qty}</TableCell>
                                        <TableCell align="right">{row.uom_name}</TableCell>
                                        {/* <TableCell align="right">{row.uom_price}</TableCell> */}
                                    </TableRow>
                                )
                            })

                        }
                    </TableBody>
                </Table>
            </TableContainer>
        )
    }

    const RenderFormProfile = () => {

        const {
            document
        } = data

        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t(`profile`)}
                            subTitle={t(`relatedProfilesAttachedToDocument`)}
                        />

                    </Grid>

                    <Grid item xs={12}>
                        <StyledDivider />
                    </Grid>

                    <Grid item xs={12} sm={12} md={12} sx={{ mt: 2 }}>
                        <Grid container spacing={0.5}>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('fromLocation')}</StyledLabel>
                            </Grid>

                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {document?.location_name}
                                </StyledContent>
                                <StyledContent2>
                                    {document?.state_name}
                                </StyledContent2>
                            </Grid>


                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('locationTo')}</StyledLabel>
                            </Grid>

                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {document?.to_location_name}
                                </StyledContent>
                                <StyledContent2>
                                    {document?.to_location_address_1} {document?.to_location_address_2}
                                    {document?.to_postcode}, {document?.to_area} {document?.to_country}
                                </StyledContent2>
                            </Grid>

                        </Grid>
                    </Grid>

                </Grid>
            </StyledCard>
        )
    }

    const RenderFormDocument = () => {
        return (
            <StyledCard>
                <Grid container spacing={0.5}>
                    <Grid item xs={12} sm={12} md={12} textAlign={"left"}>

                        <CommonSectionHeader
                            title={t(`document`)}
                            subTitle={t(`basicInformationAboutTheDocument`)}
                        />

                    </Grid>
                    <Grid item xs={12}>
                        <StyledDivider />
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} sx={{ mt: 2 }}>
                        <Grid container spacing={0.5}>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('documentNo')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document.doc_number}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>{t('documentDate')}</StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document.doc_date}
                                </StyledContent>
                            </Grid>

                            <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                                <StyledLabel>
                                    {t('remark')}
                                </StyledLabel>
                            </Grid>
                            <Grid item xs={12} sm={12} md={8} textAlign={"left"}>
                                <StyledContent>
                                    {data?.document.doc_remark}
                                </StyledContent>
                            </Grid>

                        </Grid>
                    </Grid>
                </Grid>
            </StyledCard>
        )
    }

    const RenderFormDependency = () => {
        return (
            <ViewDocumentDependency
                routeType={routeType}
                data={data?.document}
            />
        )
    }

    const RenderForm = () => {

        if (!data) return <></>

        return (
            <>
                <Grid container spacing={1}>
                    <Grid item xs={12} sm={12} md={5} textAlign={"left"}>
                        {
                            RenderFormDocument()
                        }
                    </Grid>
                    <Grid item xs={12} sm={12} md={7} textAlign={"left"}>
                        {
                            RenderFormProfile()
                        }

                        {
                            RenderFormDependency()
                        }
                    </Grid>
                </Grid>

                <Box
                    sx={{
                        textAlign: 'left',
                        mt: 3,
                        pl: 0.5,
                    }}
                >
                    <CommonSectionHeader
                        title={t(`items`)}
                        subTitle={t(`detailInformationAboutTheItemsInvolved`)}
                    />
                </Box>

                <StyledCard
                    sx={{
                        mt: 1.5,
                        p: 0
                    }}
                >
                    <Grid container rowSpacing={0}>
                        <Grid item md={12}>
                            <RenderTable />
                        </Grid>
                        {/* <Grid item md={12}>
                            <RenderTableFooter />
                        </Grid> */}

                    </Grid>
                </StyledCard>
                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"center"}>
                        <Button
                            type="button"
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            onClick={() => navigate(-1)}
                        >
                            {t('back')}
                        </Button>
                    </Grid>
                </Grid>
            </>
        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.viewTransferRequisition })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={`${t('transferRequisition')} ${data?.document.doc_number}`}
                actionMenus={
                    getBreadcumbActionMenus()
                }
                breadcrumbs={[
                    {
                        displayText: t("transferRequisitions"),
                        props: {
                            href: getRoute("documents/transferRequisitions")
                        }
                    },
                    {
                        displayText: `#${data?.document.doc_number}`,
                        props: {
                            color: 'secondary.main',
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageView.id }) ?
                    RenderForm()
                    :
                    (
                        <CommonRestricted />
                    )
            }

            {
                DocumentQrCode({
                    data,
                    outputType: scanDataOutputTypes.qrcode,
                    scopeType: scanDataScopeTypes.document,
                    targetType: scanDataTargetTypes.document.salesOrder
                })
            }

        </Box>
    )
}